import { useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";
import { Button } from "@zapier/design-system-beta";
import {
  emitEventOnInterfacesPlanUpgradeAtom,
  emitEventOnTablesPlanUpgradeAtom,
  emitEventOnChatbotsPlanUpgradeAtom,
  upgradeBannerPropsAsyncAtom,
} from "@zapier/canvas-state";
import { useTabFocus } from "../../hooks/useTabFocus";
import { getNewProductUpgradeHref } from "../../utils/getNewProductUpgradeHref";
import styles from "./UpgradeButton.module.css";

interface UpgradeButtonProps {
  isBold?: boolean;
}

export function UpgradeButton({ isBold }: UpgradeButtonProps) {
  const { tablesPlanType, interfacesPlanType, chatbotsPlanType } =
    useAtomValue(upgradeBannerPropsAsyncAtom) || {};

  useTabFocus();

  const emitEventOnTablesPlanUpgrade = useSetAtom(
    emitEventOnTablesPlanUpgradeAtom,
  );
  const emitEventOnInterfacesPlanUpgrade = useSetAtom(
    emitEventOnInterfacesPlanUpgradeAtom,
  );
  const emitEventOnChatbotsPlanUpgrade = useSetAtom(
    emitEventOnChatbotsPlanUpgradeAtom,
  );

  const handleEmitEvents = useCallback(() => {
    const TABLES_EVENT_NAME = `Upgrade Tables - ${location}`;
    const INTERFACES_EVENT_NAME = `Upgrade Interfaces - ${location}`;
    const CHATBOTS_EVENT_NAME = `Upgrade Chatbots - ${location}`;

    if (tablesPlanType) {
      emitEventOnTablesPlanUpgrade(TABLES_EVENT_NAME);
    }

    if (interfacesPlanType) {
      emitEventOnInterfacesPlanUpgrade(INTERFACES_EVENT_NAME);
    }

    if (chatbotsPlanType) {
      emitEventOnChatbotsPlanUpgrade(CHATBOTS_EVENT_NAME);
    }
  }, [
    emitEventOnInterfacesPlanUpgrade,
    emitEventOnTablesPlanUpgrade,
    emitEventOnChatbotsPlanUpgrade,
    interfacesPlanType,
    tablesPlanType,
    chatbotsPlanType,
  ]);

  const handleUpgrade = useCallback(() => {
    handleEmitEvents();
    const href = getNewProductUpgradeHref({
      tablesPlanType,
      interfacesPlanType,
      chatbotsPlanType,
    });
    window.open(href, "_blank");
  }, [interfacesPlanType, handleEmitEvents, tablesPlanType, chatbotsPlanType]);

  return (
    <Button
      className={isBold ? styles.bold : ""}
      variant={isBold ? "brand" : undefined}
      onPress={handleUpgrade}
    >
      Upgrade
    </Button>
  );
}
